import React, { useEffect, useRef, useState } from 'react';
import { Leaf, Globe, Recycle, Wind, Shield } from 'lucide-react';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

  .gp-w *, .gp-w *::before, .gp-w *::after {
    font-family: 'Outfit', sans-serif;
    box-sizing: border-box;
  }
  .gp-w h1, .gp-w h2, .gp-w h3, .gp-w h4, .gp-w h5, .gp-w h6 {
    font-family: 'Cormorant Garamond', serif;
  }

  .gp-w {
    --navy:        #0A1535;
    --navy-mid:    #0D1D45;
    --blue:        #1B4FD8;
    --blue-mid:    #2563EB;
    --blue-bright: #3B7BFF;
    --gold:        #D4A017;
    --gold-light:  #F5D080;
    --ice:         #F0F5FF;
    --ice-mid:     #DBEAFE;
    --border:      #E2E8F0;
    --text:        #1E293B;
    --muted:       #64748B;
    --dim:         #94A3B8;
    --white:       #FFFFFF;
    --green-accent:#059669;
    font-family: 'Outfit', sans-serif;
    background: var(--white);
  }

  .gp-w-section {
    background: var(--white);
    position: relative;
    overflow: hidden;
    padding: 112px 0;
  }

  /* ── BACKGROUND — matches dbe grid + washes ── */
  .gp-w-grid {
    position: absolute; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      linear-gradient(rgba(59,123,255,0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(59,123,255,0.04) 1px, transparent 1px);
    background-size: 52px 52px;
  }
  .gp-w-wash {
    position: absolute; top: -200px; right: -200px;
    width: 700px; height: 700px; border-radius: 50%;
    background: radial-gradient(circle, rgba(59,123,255,0.06) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }
  .gp-w-wash2 {
    position: absolute; bottom: -150px; left: -150px;
    width: 500px; height: 500px; border-radius: 50%;
    background: radial-gradient(circle, rgba(212,160,23,0.05) 0%, transparent 70%);
    pointer-events: none; z-index: 0;
  }

  .gp-w-topline {
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, transparent, var(--blue-mid) 30%, var(--gold) 70%, transparent);
  }

  .gp-w-inner {
    max-width: 1280px; margin: 0 auto;
    padding: 0 64px; position: relative; z-index: 2;
  }

  .gp-w-layout {
    display: flex; flex-direction: column; gap: 40px;
  }

  /* ── HERO ROW ── */
  .gp-w-hero-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 72px;
    align-items: stretch;
  }

  /* ── CHAPTER BADGE — identical to dbe-w-chapter ── */
  .gp-w-chapter {
    display: inline-flex; align-items: center; gap: 12px; margin-bottom: 28px;
  }
  .gp-w-dash { width: 36px; height: 2px; background: var(--gold); }
  .gp-w-label {
    font-family: 'Outfit', sans-serif;
    font-size: 10px; font-weight: 600; letter-spacing: 0.30em; text-transform: uppercase;
    color: var(--gold); display: flex; align-items: center; gap: 8px;
  }

  /* ── HEADLINE — identical to dbe-w-h1 ── */
  .gp-w-headline {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(46px, 5.5vw, 72px);
    font-weight: 600; line-height: 1.04;
    color: var(--navy); margin: 0 0 6px;
    letter-spacing: -0.02em;
  }
  .gp-w-headline em {
    font-style: italic; font-weight: 500;
    color: var(--blue-bright);
    padding-right: 6px;
    margin-right: -6px;
  }

  /* ── EYEBROW — identical to dbe-w-sub ── */
  .gp-w-eyebrow {
    font-family: 'Outfit', sans-serif;
    font-size: 11px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase;
    color: var(--dim); margin-bottom: 36px;
    display: flex; align-items: center; gap: 10px;
  }
  .gp-w-eyebrow::before {
    content: ''; flex: 0 0 20px; height: 1px; background: var(--border);
  }

  /* ── PARAGRAPH — identical to dbe-w-p ── */
  .gp-w-p {
    font-family: 'Outfit', sans-serif;
    font-size: 16px; line-height: 1.82;
    color: var(--muted);
    margin-bottom: 16px;
    max-width: 580px;
  }
  .gp-w-p strong { color: var(--navy); font-weight: 600; }

  /* ── CALLOUT — identical to dbe-w-callout ── */
  .gp-w-callout {
    margin: 24px 0 0 0;
    background: var(--navy);
    padding: 30px 32px;
    border-left: 4px solid var(--gold);
    position: relative; overflow: hidden;
    box-shadow: 0 16px 48px rgba(10,21,53,0.12);
  }
  .gp-w-callout::before {
    content: '"'; position: absolute; right: 16px; bottom: -24px;
    font-family: 'Cormorant Garamond', serif; font-size: 110px; font-weight: 600;
    color: rgba(255,255,255,0.05); line-height: 1; pointer-events: none;
  }
  .gp-w-callout-head {
    display: flex; align-items: center; gap: 14px; margin-bottom: 12px;
  }
  .gp-w-callout-icon {
    width: 38px; height: 38px; flex-shrink: 0;
    background: rgba(212,160,23,0.15); border: 1px solid rgba(212,160,23,0.3);
    display: flex; align-items: center; justify-content: center;
  }
  .gp-w-callout-title {
    font-family: 'Outfit', sans-serif;
    font-size: 15px; font-weight: 600; color: white;
  }
  .gp-w-callout-text {
    font-family: 'Outfit', sans-serif;
    font-size: 14px; line-height: 1.76; color: rgba(255,255,255,0.72);
  }

  /* ── FOCUS CARDS GRID ── */
  .gp-w-focus-grid {
    display: grid; grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .gp-w-focus-card {
    background: var(--ice);
    border: 1.5px solid var(--ice-mid);
    border-top: 4px solid var(--blue-mid);
    padding: 26px 24px;
    position: relative; overflow: hidden;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
    cursor: default;
    display: flex; flex-direction: column;
  }
  .gp-w-focus-card::after {
    content: '';
    position: absolute; top: -30px; right: -30px;
    width: 90px; height: 90px; border-radius: 50%;
    background: radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%);
  }
  .gp-w-focus-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 48px rgba(37,99,235,0.12);
    border-color: var(--blue-mid);
  }

  .gp-w-focus-icon-wrap {
    width: 38px; height: 38px; margin-bottom: 14px;
    background: var(--ice-mid);
    border: 1px solid rgba(37,99,235,0.2);
    display: flex; align-items: center; justify-content: center;
    position: relative; z-index: 1; flex-shrink: 0;
  }

  .gp-w-focus-num {
    position: absolute; top: 16px; right: 18px;
    font-family: 'Cormorant Garamond', serif;
    font-size: 44px; font-weight: 600;
    color: rgba(37,99,235,0.05); line-height: 1;
    pointer-events: none;
  }

  .gp-w-focus-card-title {
    font-family: 'Outfit', sans-serif;
    font-size: 14px; font-weight: 600;
    color: var(--navy);
    margin-bottom: 10px;
    position: relative; z-index: 1;
  }

  .gp-w-focus-card-text {
    font-family: 'Outfit', sans-serif;
    font-size: 12.5px; line-height: 1.78;
    color: var(--muted);
    position: relative; z-index: 1;
    flex: 1;
  }

  /* ── QBAR — identical to dbe-w-qbar ── */
  .gp-w-qbar {
    padding: 18px 24px;
    background: var(--ice); border: 1px solid var(--ice-mid);
    border-left: 4px solid var(--blue-mid);
    display: flex; gap: 14px; align-items: flex-start;
  }
  .gp-w-qbar-text {
    font-family: 'Outfit', sans-serif;
    font-size: 14px; line-height: 1.74;
    color: var(--muted); font-style: italic;
  }
  .gp-w-qbar-text strong { color: var(--navy); font-style: normal; font-weight: 600; }

  /* ── SEED NOTE ── */
  .gp-w-seed {
    padding: 18px 24px;
    background: var(--navy);
    border-left: 4px solid var(--gold);
    display: flex; gap: 14px; align-items: flex-start;
    box-shadow: 0 16px 48px rgba(10,21,53,0.10);
  }
  .gp-w-seed-icon {
    width: 34px; height: 34px; flex-shrink: 0;
    background: rgba(212,160,23,0.15); border: 1px solid rgba(212,160,23,0.3);
    display: flex; align-items: center; justify-content: center;
  }
  .gp-w-seed-text {
    font-family: 'Outfit', sans-serif;
    font-size: 14px; line-height: 1.74;
    color: rgba(255,255,255,0.78);
  }
  .gp-w-seed-text strong { color: var(--gold-light); font-weight: 600; }

  /* ── ANIMATIONS ── */
  .gp-fade { opacity: 0; transform: translateY(28px); transition: opacity 0.8s ease, transform 0.8s ease; }
  .gp-fade.on { opacity: 1; transform: none; }
  .gp-fade-r { opacity: 0; transform: translateX(36px); transition: opacity 0.9s ease, transform 0.9s ease; }
  .gp-fade-r.on { opacity: 1; transform: none; }

  /* ── RESPONSIVE ── */
  @media (max-width: 1100px) {
    .gp-w-inner { padding: 0 28px; }
    .gp-w-hero-row { grid-template-columns: 1fr; gap: 48px; }
  }
  @media (max-width: 640px) {
    .gp-w-section { padding: 80px 0; }
    .gp-w-focus-grid { grid-template-columns: 1fr; }
  }
`;

const focusAreas = [
  {
    icon: <Leaf size={16} color="#2563EB" />,
    title: 'Sustainable Lifestyle',
    text: 'Encouraging sustainable lifestyle choices among consumers and partners for lasting positive impact.',
    num: '01',
  },
  {
    icon: <Recycle size={16} color="#2563EB" />,
    title: 'Eco-Friendly Practices',
    text: 'Supporting environmentally conscious product practices that reduce harm at every stage of the value chain.',
    num: '02',
  },
  {
    icon: <Globe size={16} color="#2563EB" />,
    title: 'Awareness & Education',
    text: 'Promoting awareness about reducing environmental impact through informed choices and community engagement.',
    num: '03',
  },
  {
    icon: <Wind size={16} color="#2563EB" />,
    title: 'Ecological Balance',
    text: 'Contributing to long-term ecological balance through responsible business operations and stewardship.',
    num: '04',
  },
];

const GreenPlanetSection = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold: 0.07 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <section className="gp-w gp-w-section" ref={ref}>
        <div className="gp-w-grid" />
        <div className="gp-w-wash" />
        <div className="gp-w-wash2" />
        <div className="gp-w-topline" />
        <div className="gp-w-inner">
          <div className="gp-w-layout">
 
            {/* ── HERO ROW ── */}
            <div className="gp-w-hero-row">

              {/* LEFT col — headline + text + callout */}
              <div className={`gp-fade ${vis ? 'on' : ''}`} style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="gp-w-chapter">
                  <div className="gp-w-dash" />
                  <span className="gp-w-label"><Leaf size={11} color="#D4A017" /> Environmental Commitment</span>
                </div>
                <h2 className="gp-w-headline">Green <em>Planet</em><br />Initiative</h2>
                <p className="gp-w-eyebrow">Sustainability · Ecosystem · Responsibility</p>
                <p className="gp-w-p">
                  IndusViva is committed to <strong>environmental responsibility</strong> through its Green Planet Initiative — focusing on sustainable practices that contribute to a healthier ecosystem.
                </p>
                <p className="gp-w-p">
                  The initiative promotes <strong>awareness, responsible consumption</strong>, and eco-friendly actions within the community, reflecting the belief that true growth must go hand-in-hand with <strong>environmental stewardship</strong>.
                </p>

                {/* Callout pinned to bottom */}
                <div className="gp-w-callout" style={{ marginTop: 'auto' }}>
                  <div className="gp-w-callout-head">
                    <div className="gp-w-callout-icon"><Globe size={18} color="#D4A017" /></div>
                    <span className="gp-w-callout-title">True Growth &amp; Stewardship</span>
                  </div>
                  <p className="gp-w-callout-text">"True growth must go hand-in-hand with environmental stewardship — creating a legacy that benefits both people and the planet."</p>
                </div>
              </div>

              {/* RIGHT col — 4 focus cards */}
              <div
                className={`gp-fade-r ${vis ? 'on' : ''}`}
                style={{ transitionDelay: '150ms', display: 'flex', flexDirection: 'column', gap: '14px' }}
              >
                <div className="gp-w-focus-grid">
                  {focusAreas.map((item, i) => (
                    <div
                      className={`gp-w-focus-card gp-fade ${vis ? 'on' : ''}`}
                      key={item.num}
                      style={{ transitionDelay: `${200 + i * 80}ms` }}
                    >
                      <div className="gp-w-focus-num">{item.num}</div>
                      <div className="gp-w-focus-icon-wrap">{item.icon}</div>
                      <div className="gp-w-focus-card-title">{item.title}</div>
                      <p className="gp-w-focus-card-text">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── QBAR — full width below ── */}
            <div className={`gp-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '200ms' }}>
              <div className="gp-w-qbar">
                <p className="gp-w-qbar-text">
                  This initiative reflects IndusViva's belief that <strong>true growth</strong> must go hand-in-hand with environmental stewardship — contributing to a healthier ecosystem for <strong>generations to come</strong>.
                </p>
              </div>
            </div>

            {/* ── SEED PACKET NOTE — full width below QBAR ── */}
            <div className={`gp-fade ${vis ? 'on' : ''}`} style={{ transitionDelay: '260ms' }}>
              <div className="gp-w-seed">
                <div className="gp-w-seed-icon"><Leaf size={16} color="#D4A017" /></div>
                <p className="gp-w-seed-text">
                  As part of the <strong>Green Planet Initiative</strong>, every VibrantViva product ships with a <strong>seed packet</strong> — a small, everyday way for our community to give back to the earth with each purchase.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default GreenPlanetSection;